// ═══════════════════════════════════════════════════════════════
// BOX — Terminal de autoconsulta de agencia
// El cliente arma su apuesta solo y saca un código QP + QR para pagar
// en el mostrador. Ruta: /box/AGE002
// Solo prematch: las cuotas en vivo cambian y podrían vencer antes de
// llegar a la caja.
// ═══════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";
import { getFrontendConfig } from "./config";
import { horaLocal } from "./fechas";
import { canjearCodigo, identificarse, olvidarToken,
         cabeceraDeTerminal } from "./credencialTerminal";
import { oscuro as Q, F_NUM, F_BODY, RADII, SPACING, ov } from "./theme";
import { money } from "./moneda";

// Los desafios NO estan en ninguna moneda: su plata se mueve por
// `_p2p_mover`, que escribe `users.saldo_iacoin` -columna distinta de
// `users.balance`, que es la plata cuya moneda es `users.moneda`-.
//
// El `moneda` que viaja en estas cargas es el de la AGENCIA DEL CREADOR,
// no la unidad del monto. Es util -IACOIN tiene precio por moneda en
// `iacoin_cotizaciones`- pero no describe estas cifras. Rotularlas con el
// simbolo de su moneda las muestra con una escala que no es la suya.
const iacoin = n => Number(n || 0).toLocaleString("es-AR") + " IACOIN";

import Icon from "./Icon";
import { Handshake, Repeat, Moon, Image as ImageIcon, Pencil, Wrench, Dices, Scale, Rocket, Inbox } from "lucide-react";
import BrandMark from "./BrandMark";
// La cuenta del mismo partido es una regla de precio compartida con el
// servidor, así que no se vuelve a escribir acá. Ver
// `odd/tasks/cotizacion-mismo-partido.md`.
import { hayMismoPartido } from "./combosMismoPartido";
import { conRef, envioCumplido, mensajeDeConflicto } from "./refDeApuesta";

// La hora del partido, en la zona horaria del dispositivo.
// Se prefiere commence_time (ISO con zona) sobre el texto ya
// formateado que manda el servidor: el navegador sabe dónde está
// el usuario y no hay que adivinarlo por IP.
// `horaLocal` vive en `fechas.js`: era idéntica en cuatro pantallas y el
// panel del admin no la tenía, así que ahí la hora salía en la zona de la
// casa y no en la de quien miraba.

// ── EL NOMBRE DEL EMISOR DE LA FILA FIJA DE 1X2 ───────────────
//
// Las tres pantallas que dibujan el 1X2 con el atajo `ev.odds.L/E/V` —acá,
// `Web.jsx` y el armador de combos del admin— necesitan el NOMBRE con el que
// GR8 emite ese resultado, que es lo único que `resolver_una` encuentra en el
// índice de claves de selección. `ev.odds` no lo trae: lo arma
// `gr8_catalogo._odds_compactas` colapsando `h2h_3_way` y `h2h` en tres
// posiciones, así que mirándolo no se puede saber de qué mercado salió.
//
// Lo que sí trae el catálogo es `ev.markets`, el diccionario del emisor. Acá se
// lee UNA clave literal —`h2h`— y el nombre sale de adentro de ese mercado. No
// se deriva nada: no se prueba `h2h_3_way` "por si acaso" ni se traduce
// "Empate" a "Draw" con una tabla nuestra, porque la etiqueta de pantalla no es
// inyectiva sobre los nombres del emisor y la mejor tabla posible acierta 1 de
// 4 (medido en L3).
//
// Cuando el nombre NO está en ese diccionario, el nombre y el mercado van los
// dos VACÍOS. Es el caso del 1X2 del fútbol, que GR8 emite en `h2h_3_way`: el
// pick sigue sin liquidar —como antes— pero no miente diciendo que GR8 publicó
// ese resultado en `h2h`. Un pick sin clave es honesto; uno con la clave
// inventada se liquida contra la pregunta que el jugador no hizo. Es la misma
// regla y el mismo repliegue que `OddsButtons` (`cuotasDeEvento.jsx:152`).
//
// Van juntos a propósito: los dos salen del MISMO diccionario, así que mandar
// uno sin el otro sería afirmar la mitad de algo que no se leyó.
//
// Está copiada textual en `Web.jsx` y en `Admin.jsx` —no pueden importar de
// acá sin arrastrarse el sitio entero al bundle, igual que `picksCotizados`—
// y `losCombosSeLiquidan.test.js` ejecuta las tres y exige que den lo mismo.
export function nombresDe1x2(ev){
  const mkt = (ev?.markets || {}).h2h || {};
  const home = ev?.h || ev?.home || "";
  const away = ev?.a || ev?.away || "";
  const de = (nombre) => (nombre in mkt
    ? { nombre, market: "h2h" }
    : { nombre: "", market: "" });
  return { L: de(home), E: de("Draw"), V: de(away) };
}

const { apiUrl: API } = getFrontendConfig();

// Atajo para los montos que todavia no saben de que moneda son. Delega en
// el formateador unico; M4-M6 cambian cada llamada por money(n, moneda).
// Ya no hay `ars` acá: M5 dejó el mostrador sin un solo monto que no
// sepa su moneda.

// Montos sugeridos según la moneda. 20.000 es una apuesta corriente en
// pesos y una barbaridad en dólares, así que la escala se ajusta.
// Devuelve siempre 4 valores para las fichas rápidas.
const MONTOS_POR_MONEDA = {
  ARS: [2000, 5000, 10000, 20000],
  CLP: [2000, 5000, 10000, 20000],
  COP: [5000, 10000, 20000, 50000],
  PYG: [10000, 20000, 50000, 100000],
  USD: [5, 10, 20, 50],
  EUR: [5, 10, 20, 50],
  BRL: [10, 25, 50, 100],
  UYU: [100, 250, 500, 1000],
  PEN: [10, 25, 50, 100],
  MXN: [50, 100, 250, 500],
};
function montosDe(moneda){
  return MONTOS_POR_MONEDA[(moneda||"ARS").toUpperCase()]
      || MONTOS_POR_MONEDA.ARS;
}
// El monto inicial: el segundo de la lista, ni el mínimo ni el máximo.
function montoInicial(moneda){ return montosDe(moneda)[1]; }

const fmt = n => Number(n||0).toFixed(2);

function TeamLogo({ name, size=30 }){
  const [fallo,setFallo]=useState(false);
  const ini=(name||"?").split(" ").filter(Boolean).slice(0,2)
    .map(w=>w[0].toUpperCase()).join("");
  let h=0; for(let i=0;i<(name||"").length;i++) h=((h<<5)-h)+name.charCodeAt(i);
  const colors=["#2B6BFF","#7B3FE4","#0E8F6F","#C4162A","#1B3A8C","#E07A1F"];
  const color=colors[Math.abs(h)%colors.length];
  if(name&&!fallo) return(
    <div style={{width:size,height:size,borderRadius:"50%",flexShrink:0,
      background:Q.inset,border:`1px solid ${Q.border}`,
      display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
      <img src={`${API}/api/team-logo/nombre/${encodeURIComponent(name)}`}
        alt={name} loading="lazy" onError={()=>setFallo(true)}
        style={{width:"78%",height:"78%",objectFit:"contain"}}/>
    </div>
  );
  return(
    <div style={{width:size,height:size,borderRadius:"50%",flexShrink:0,
      background:`linear-gradient(135deg,${color},${color}88)`,
      display:"flex",alignItems:"center",
      justifyContent:"center"}}>
      <span style={{color:"#fff",fontWeight:700,fontSize:size*0.42,
        fontFamily:F_NUM,letterSpacing:0.3}}>{ini||"?"}</span>
    </div>
  );
}

// ── CORREGIR PICK (box) ───────────────────────────────────────
// Avisos que publica la administración. Rotan solos y se descartan
// tocándolos: no son bloqueantes, así que nunca tapan la operación.
// El QR de esta terminal, fijo abajo a la derecha. El cliente lo
// escanea y arma su boleto en el teléfono mientras espera: llega al
// mostrador con el código listo y el cajero solo cobra.
// Los desafíos abiertos, en la pantalla del local. Solo lectura: el
// cajero no puede crear ni aceptar porque los desafíos son entre
// jugadores con cuenta y saldo propio.
//
// Sirve para generar conversación —"Juan puso 5.000 a que gana
// River"— y empujar a que la gente escanee el QR.
function DesafiosBox({ agenciaCode }){
  const [lista,setLista]=useState([]);
  const [i,setI]=useState(0);

  useEffect(()=>{
    let vivo=true;
    const traer=()=>{
      fetch(`${API}/api/p2p/abiertas?limite=12`)
        .then(r=>r.ok?r.json():null)
        .then(d=>{ if(vivo&&d?.activo) setLista(d.apuestas||[]); })
        .catch(()=>{});
    };
    traer();
    const t=setInterval(traer,90000);
    return()=>{ vivo=false; clearInterval(t); };
  },[]);

  // Rota solo: en una pantalla de local nadie va a tocar para pasar
  useEffect(()=>{
    if(lista.length<2) return;
    const t=setInterval(()=>setI(x=>(x+1)%lista.length),8000);
    return()=>clearInterval(t);
  },[lista.length]);

  if(!lista.length) return null;
  const d=lista[i%lista.length];

  return(
    <div style={{background:`linear-gradient(135deg,${Q.gold}14,${Q.violet}0D)`,
      border:`1px solid ${Q.gold}44`,borderRadius:RADII.lg,
      padding:"12px 16px",marginBottom:12}}>
      <div style={{display:"flex",justifyContent:"space-between",
        alignItems:"center",marginBottom:7}}>
        <span style={{color:Q.gold,fontSize:12,fontWeight:800,
          letterSpacing:0.8}}><Handshake size={11} aria-hidden="true"/> DESAFÍOS ABIERTOS</span>
        {lista.length>1&&(
          <span style={{color:Q.dim,fontSize:12}}>
            {i+1} de {lista.length}</span>
        )}
      </div>

      <div style={{color:Q.text,fontSize:14,fontWeight:600,
        lineHeight:1.4}}>{d.titulo}</div>
      <div style={{color:Q.muted,fontSize:12,marginTop:4}}>
        {d.creador} puso {iacoin(d.pone_el_creador)}
        {" · "}busca quien ponga {iacoin(d.tenes_que_poner)}</div>

      <div style={{color:Q.cyan,fontSize:12,marginTop:8,paddingTop:SPACING[8],
        borderTop:`1px solid ${Q.border}`,lineHeight:1.45}}>
        Escaneá el código QR para aceptarlo desde tu celular</div>
    </div>
  );
}

function QRTerminal({ agenciaCode }){
  const [term,setTerm]=useState(null);
  const [grande,setGrande]=useState(false);

  useEffect(()=>{
    if(!agenciaCode) return;
    // El Box no maneja sesión de agencia, así que la terminal se
    // consulta por el código del local.
    fetch(`${API}/api/box/${agenciaCode}/terminal`)
      .then(r=>r.ok?r.json():null)
      .then(d=>{ if(d?.terminal) setTerm(d.terminal); })
      .catch(()=>{});
  },[agenciaCode]);

  if(!term) return null;

  const qr=(px)=>`https://api.qrserver.com/v1/create-qr-code/?size=${px}x${px}&margin=10&data=${encodeURIComponent(term.url)}`;

  if(grande){
    return(
      <div onClick={()=>setGrande(false)} style={{position:"fixed",
        inset:0,zIndex:500,background:"rgba(2,2,8,.94)",display:"flex",
        flexDirection:"column",alignItems:"center",
        justifyContent:"center",padding:SPACING[20],cursor:"pointer"}}>
        <div style={{color:Q.text,fontSize:22,fontWeight:800,
          marginBottom:16,textAlign:"center"}}>
          Apostá desde tu celular</div>
        <img alt="QR" src={qr(480)}
          style={{width:"min(72vw,420px)",background:"#fff",
            borderRadius:RADII.lg,padding:SPACING[16]}}/>
        <div style={{color:Q.gold,fontSize:26,fontWeight:900,
          letterSpacing:2,marginTop:14}}>{term.codigo}</div>
        <div style={{color:Q.muted,fontSize:14,marginTop:10,
          textAlign:"center",lineHeight:1.5,maxWidth:420}}>
          Escaneá, armá tu boleto y vení al mostrador con el código.
          <br/>{term.nombre}</div>
        <div style={{color:Q.dim,fontSize:12,marginTop:18}}>
          Tocá para cerrar</div>
      </div>
    );
  }

  return(
    <div onClick={()=>setGrande(true)}
      style={{position:"fixed",right:14,
        bottom:"calc(14px + env(safe-area-inset-bottom))",zIndex:70,
        background:"rgba(10,10,22,.92)",border:`1px solid ${Q.border}`,
        borderRadius:RADII.lg,padding:"12px 12px",cursor:"pointer",
        display:"flex",alignItems:"center",gap:SPACING[12],
        backdropFilter:"blur(8px)",maxWidth:230}}>
      <img alt="QR" src={qr(120)}
        style={{width:58,height:58,background:"#fff",borderRadius:RADII.sm,
          padding:SPACING[4],flexShrink:0}}/>
      <div style={{minWidth:0}}>
        <div style={{color:Q.text,fontSize:12,fontWeight:700,
          lineHeight:1.35}}>
          ¿Querés apostar<br/>desde tu celular?</div>
        <div style={{color:Q.cyan,fontSize:12,marginTop:3}}>
          Escaneá este código</div>
      </div>
    </div>
  );
}

function AvisosBanner({ destino, agenciaCode }){
  const [avisos,setAvisos]=useState([]);
  const [i,setI]=useState(0);
  const [cerrados,setCerrados]=useState(new Set());

  useEffect(()=>{
    let vivo=true;
    const traer=()=>{
      const q=agenciaCode?`?destino=${destino}&agencia_code=${agenciaCode}`
                         :`?destino=${destino}`;
      fetch(`${API}/api/avisos${q}`)
        .then(r=>r.ok?r.json():null)
        .then(d=>{ if(vivo&&d) setAvisos(d.avisos||[]); })
        .catch(()=>{});
    };
    traer();
    const t=setInterval(traer,180000);
    return()=>{ vivo=false; clearInterval(t); };
  },[destino,agenciaCode]);

  const visibles=avisos.filter(a=>!cerrados.has(a.id));

  useEffect(()=>{
    if(visibles.length<2) return;
    const t=setInterval(()=>setI(x=>(x+1)%visibles.length),6000);
    return()=>clearInterval(t);
  },[visibles.length]);

  if(!visibles.length) return null;
  const a=visibles[Math.min(i,visibles.length-1)];
  const COLOR={info:"#00F0FF", aviso:"#E8C547", urgente:"#FF3B5C"};
  const c=COLOR[a.nivel]||COLOR.info;

  return(
    <div style={{background:`${c}12`,border:`1px solid ${c}44`,
      borderRadius:RADII.md,padding:"12px 12px",marginBottom:10,
      display:"flex",alignItems:"flex-start",gap:SPACING[8]}}>
      <div style={{minWidth:0,flex:1}}>
        <div style={{color:c,fontWeight:700,fontSize:12}}>{a.titulo}</div>
        {a.cuerpo&&(
          <div style={{color:Q.text,fontSize:12,marginTop:3,
            lineHeight:1.5}}>{a.cuerpo}</div>
        )}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:SPACING[8],flexShrink:0}}>
        {visibles.length>1&&(
          <div style={{display:"flex",gap:SPACING[4]}}>
            {visibles.map((_,k)=>(
              <span key={k} style={{width:5,height:5,borderRadius:RADII.sm,
                background:k===i?c:Q.border}}/>
            ))}
          </div>
        )}
        <button onClick={()=>setCerrados(s=>new Set([...s,a.id]))}
          aria-label="Cerrar aviso"
          style={{background:"transparent",border:"none",color:Q.muted,
            fontSize:16,cursor:"pointer",padding:0,lineHeight:1}}>×</button>
      </div>
    </div>
  );
}

function CorregirPickBox({ pick, onAplicar, onQuitar }){
  const [modo,setModo]=useState("seleccion");
  const [buscar,setBuscar]=useState("");
  const [encontrados,setEncontrados]=useState([]);
  const [buscando,setBuscando]=useState(false);
  const [ajuste,setAjuste]=useState("");
  const opciones=pick.opciones||[];

  const buscarEventos=async()=>{
    if(buscar.trim().length<2||buscando) return;
    setBuscando(true);
    try{
      const r=await fetch(`${API}/api/buscar-eventos?q=${encodeURIComponent(buscar.trim())}`);
      const d=await r.json();
      setEncontrados(d.eventos||[]);
    }catch(e){ setEncontrados([]); }
    setBuscando(false);
  };
  // Lo mismo que en `CorregirPickApp` (`App.jsx`), y por lo mismo: `op.sel` y
  // `op.mkt` son las ETIQUETAS que el cajero lee, `op.nombre` y `op.market` son
  // los nombres que EMITE GR8 y los únicos que el índice de claves de selección
  // conoce. Mandando sólo las etiquetas, el boleto se emitía y después no se
  // podía liquidar por clave: lo terminaba pagando una persona en el mostrador,
  // que acá es la misma persona que lo acaba de cargar. Ver E5a-1b.
  const elegirOpcion=(op)=>{
    const cuota=ajuste.trim()?parseFloat(ajuste):op.odd;
    onAplicar({selection:op.sel,nombre:op.nombre,market:op.market,
      odd_nuestra:op.odd,odd_final:cuota,
      odd_ajustada:cuota,ajustada:!!ajuste.trim(),estado:"ok"});
  };
  const elegirNuevo=(ev,op)=>{
    const cuota=ajuste.trim()?parseFloat(ajuste):op.odd;
    onAplicar({home:ev.home,away:ev.away,home_real:ev.home,away_real:ev.away,
      event_id:ev.event_id,sport_key:ev.sport_key,selection:op.sel,
      nombre:op.nombre,market:op.market,
      odd_nuestra:op.odd,odd_final:cuota,odd_ajustada:cuota,
      ajustada:!!ajuste.trim(),estado:"ok",opciones:ev.opciones});
  };

  return(
    <div style={{marginTop:10,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`}}>
      {(pick.home_real||pick.away_real)&&(
        <div style={{color:Q.muted,fontSize:12,marginBottom:8}}>
          En nuestro sistema: <span style={{color:Q.cyan}}>{pick.home_real} vs {pick.away_real}</span>
        </div>
      )}
      {/* Candidatos parecidos (cuando no encontró el partido) */}
      {(pick.candidatos&&pick.candidatos.length>0)&&(
        <div style={{marginBottom:10}}>
          <div style={{color:Q.amber,fontSize:12,marginBottom:6,
            fontFamily:F_BODY}}>
            ¿Quisiste decir alguno de estos?
          </div>
          {pick.candidatos.map((ev,ci)=>(
            <div key={ci} style={{background:`${Q.amber}0E`,
              border:`1px solid ${Q.amber}44`,borderRadius:RADII.md,
              padding:"8px 12px",marginBottom:6}}>
              <div style={{color:Q.text,fontSize:12,fontWeight:600,marginBottom:5,
                fontFamily:F_BODY}}>{ev.home} vs {ev.away}</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[4]}}>
                {(ev.opciones||[]).map((op,j)=>(
                  <button key={j} onClick={()=>elegirNuevo(ev,op)} style={{
                    background:ov(0.05),border:`1px solid ${Q.border}`,
                    borderRadius:RADII.sm,padding:"4px 8px",cursor:"pointer",
                    color:Q.cyan,fontSize:12,fontWeight:600,
                    fontFamily:F_BODY}}>
                    {op.sel} · {fmt(op.odd)}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
        <button onClick={()=>setModo("seleccion")} style={{flex:1,
          background:modo==="seleccion"?`${Q.violet}33`:ov(0.04),
          border:`1px solid ${modo==="seleccion"?Q.violet:Q.border}`,borderRadius:RADII.md,
          padding:"8px",cursor:"pointer",color:modo==="seleccion"?Q.cyan:Q.muted,
          fontSize:12,fontWeight:700}}>Cambiar selección</button>
        <button onClick={()=>setModo("partido")} style={{flex:1,
          background:modo==="partido"?`${Q.violet}33`:ov(0.04),
          border:`1px solid ${modo==="partido"?Q.violet:Q.border}`,borderRadius:RADII.md,
          padding:"8px",cursor:"pointer",color:modo==="partido"?Q.cyan:Q.muted,
          fontSize:12,fontWeight:700}}>Otro partido</button>
      </div>

      {modo==="seleccion"&&(
        <div>
          {opciones.length===0&&<div style={{color:Q.muted,fontSize:12,marginBottom:8}}>
            No tenemos opciones de este partido. Probá "Otro partido".</div>}
          {opciones.map((op,k)=>(
            <button key={k} onClick={()=>elegirOpcion(op)} style={{width:"100%",
              display:"flex",justifyContent:"space-between",alignItems:"center",
              background:ov(0.04),border:`1px solid ${Q.border}`,
              borderRadius:RADII.md,padding:"8px 12px",marginBottom:5,cursor:"pointer"}}>
              <span style={{color:Q.text,fontSize:12}}>{op.sel}</span>
              <span style={{color:Q.cyan,fontWeight:700,fontSize:13}}>{fmt(op.odd)}</span>
            </button>
          ))}
        </div>
      )}

      {modo==="partido"&&(
        <div>
          <div style={{display:"flex",gap:SPACING[8],marginBottom:8}}>
            <input value={buscar} onChange={e=>setBuscar(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&buscarEventos()}
              placeholder="Nombre del equipo..."
              style={{flex:1,background:ov(0.05),
                border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
                color:Q.text,fontSize:14}}/>
            <button onClick={buscarEventos} style={{background:`${Q.violet}33`,
              border:`1px solid ${Q.violet}`,borderRadius:RADII.md,padding:"0 16px",
              cursor:"pointer",color:Q.cyan,fontSize:12,fontWeight:700}}>
              {buscando?"...":"Buscar"}</button>
          </div>
          {encontrados.map((ev,k)=>(
            <div key={k} style={{background:ov(0.03),
              border:`1px solid ${Q.dim}`,borderRadius:RADII.md,padding:"8px 12px",marginBottom:6}}>
              <div style={{color:Q.text,fontSize:12,fontWeight:600,marginBottom:6}}>
                {ev.home} vs {ev.away}</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[4]}}>
                {(ev.opciones||[]).map((op,j)=>(
                  <button key={j} onClick={()=>elegirNuevo(ev,op)} style={{
                    background:ov(0.05),border:`1px solid ${Q.border}`,
                    borderRadius:RADII.sm,padding:"4px 8px",cursor:"pointer",
                    color:Q.cyan,fontSize:12,fontWeight:600}}>
                    {op.sel} · {fmt(op.odd)}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{display:"flex",gap:SPACING[8],marginTop:8}}>
        <input value={ajuste} onChange={e=>setAjuste(e.target.value)}
          placeholder="Cuota a mano (opcional)" inputMode="decimal"
          style={{flex:1,background:ov(0.05),
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.text,fontSize:13}}/>
        <button onClick={onQuitar} style={{background:`${Q.pink}18`,
          border:`1px solid ${Q.pink}`,borderRadius:RADII.md,padding:"8px 12px",
          cursor:"pointer",color:Q.pink,fontSize:12,fontWeight:700}}>Quitar</button>
      </div>
    </div>
  );
}

export default function Box(){
  // Código de agencia desde la URL: /box/AGE002
  const agenciaCode = (window.location.pathname.split("/")[2]||"").toUpperCase();

  const [agencia,setAgencia]=useState(null);
  const mon = agencia?.moneda || "ARS";
  const [errAg,setErrAg]=useState("");
  const [deportes,setDeportes]=useState(null);
  const [liga,setLiga]=useState(null);
  const [busqueda,setBusqueda]=useState("");
  const [picks,setPicks]=useState([]);
  // Arranca en pesos y se ajusta cuando llega la agencia con su moneda:
  // el dato del backend no está disponible en el primer render.
  const [monto,setMonto]=useState(montoInicial("ARS"));
  const [montoTocado,setMontoTocado]=useState(false);
  const [bbFeature,setBbFeature]=useState({activo:false,min_picks:2});
  // Generar combinada: en el box no hay cliente identificado, así que
  // el cupo es de la agencia entera. Sirve para evitar abuso, no como
  // límite por persona.
  const [genProc,setGenProc]=useState(false);
  const [genCupo,setGenCupo]=useState(null);

  const generarCombo=async(perfil)=>{
    if(genProc) return;
    setGenProc(true);
    try{
      // `cabeceraDeTerminal` SUMADA al `Content-Type`: el cupo que se
      // consume lo decide ahora la credencial y no el `agencia_code` del
      // cuerpo, así que sin ella el servidor contesta 401. El código sigue
      // viajando para no cambiarle el cuerpo a una pantalla que ya existe,
      // pero dejó de elegir a qué agencia se le gasta el cupo del día.
      const ru=await fetch(`${API}/api/combo-sugerido/usar`,{
        method:"POST",
        headers:{"Content-Type":"application/json",
                 ...cabeceraDeTerminal(agenciaCode)},
        body:JSON.stringify({agencia_code:agenciaCode})});
      const du=await ru.json().catch(()=>({}));
      if(!ru.ok) throw new Error(du.detail||"Sin cupo");
      const r=await fetch(
        `${API}/api/combo-sugerido?perfil=${perfil}&agencia_code=${agenciaCode}`);
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||"No se pudo armar");
      // `market:""` y no `"h2h"`. `/api/combo-sugerido` arma el `sel` con
      // texto propio (`f"{fav_team} gana"`) y no devuelve ni el mercado ni el
      // nombre del emisor, así que acá no hay nada que afirmar: dejar `"h2h"`
      // clavado es mandar un mercado que nadie leyó —y para el 1X2 del fútbol,
      // el equivocado—. El combo sigue sin liquidar solo, igual que antes.
      setPicks(d.picks.map(p=>({id:p.event_id,event_id:p.event_id,
        h:p.h,a:p.a,home:p.h,away:p.a,sel:p.sel,odd:p.odd,
        market:"",sport_key:p.sport_key,
        commence_time:p.commence_time})));
      // `cabeceraDeTerminal`, igual que el `usar` de arriba: el cupo pasó a
      // pedir credencial. Va directa y no sumada porque un GET no lleva
      // `Content-Type` que haya que preservar, que es la misma forma que ya
      // usa el cashout de la ventanilla. El código sigue viajando y dejó de
      // elegir de qué agencia se lee el cupo.
      fetch(`${API}/api/combo-sugerido/cupo?agencia_code=${agenciaCode}`,
            {headers:cabeceraDeTerminal(agenciaCode)})
        .then(x=>x.ok?x.json():null).then(x=>x&&setGenCupo(x)).catch(()=>{});
    }catch(e){ alert(e.message); }
    setGenProc(false);
  };

  // Si la agencia opera en otra moneda, el monto sugerido se adapta.
  // No se pisa lo que el cajero haya escrito a mano.
  useEffect(()=>{
    if(agencia?.moneda && !montoTocado) setMonto(montoInicial(agencia.moneda));
  },[agencia?.moneda, montoTocado]);
  useEffect(()=>{
    if(!agenciaCode) return;
    fetch(`${API}/api/bet-builder/config?agencia=${agenciaCode}`)
      .then(r=>r.ok?r.json():null).then(d=>{ if(d) setBbFeature(d); }).catch(()=>{});
  },[agenciaCode]);
  const [pantalla,setPantalla]=useState("armar");  // armar | listo
  const [slip,setSlip]=useState(null);
  const [enviando,setEnviando]=useState(false);
  const [errEnvio,setErrEnvio]=useState("");
  const [seccion,setSeccion]=useState("armar");    // armar | combos | mejorar | cashout

  // ── La credencial de esta terminal ────────────────────────────
  //
  // Antes esta pantalla no tenía identidad: lo único que sabía de sí misma era
  // el código de la URL (/box/AGE002), y una URL no es una credencial porque la
  // escribe cualquiera. Por eso el cash out del Box necesitaba un endpoint
  // abierto, y por eso ese endpoint era el agujero.
  //
  // Ahora la agencia emite un código de un solo uso desde su panel, alguien lo
  // tipea UNA vez acá, y la pantalla queda con un token propio. Dónde se guarda
  // y por qué está razonado en `credencialTerminal.js`: en `localStorage`, al
  // contrario de la mini-app de Telegram, porque una terminal de mostrador se
  // reinicia y tiene que seguir siendo ella misma sin que nadie vuelva a cargar
  // nada.
  //
  // `terminal` es la identidad resuelta por el servidor —no por el código de la
  // URL—, y `puede_cashout` sale de ahí: es lo que decide si se muestra el
  // botón. No autoriza nada; el permiso se vuelve a verificar al cashear.
  const [terminal,setTerminal]=useState(null);
  const [credProbando,setCredProbando]=useState(true);
  const [codigoAlta,setCodigoAlta]=useState("");
  const [altaMsg,setAltaMsg]=useState("");
  const [altaProc,setAltaProc]=useState(false);

  useEffect(()=>{
    if(!agenciaCode) return;
    let vivo=true;
    setCredProbando(true);
    identificarse(API, agenciaCode).then(r=>{
      if(!vivo) return;
      setTerminal(r.ok?r.terminal:null);
      setCredProbando(false);
    });
    return()=>{ vivo=false; };
  },[agenciaCode]);

  const darDeAlta=async()=>{
    if(altaProc) return;
    setAltaProc(true); setAltaMsg("");
    const r=await canjearCodigo(API, agenciaCode, codigoAlta);
    if(r.ok){
      setCodigoAlta("");
      // Se vuelve a preguntar quién es en vez de confiar en lo que devolvió el
      // canje: así la pantalla arranca con exactamente los mismos datos que va
      // a tener en cada reinicio, y `puede_cashout` sale de un solo lugar.
      const yo=await identificarse(API, agenciaCode);
      setTerminal(yo.ok?yo.terminal:null);
      if(!yo.ok) setAltaMsg("Se dio de alta pero no pudimos confirmarla. "
                            +"Recargá la pantalla.");
    }else{
      setAltaMsg(r.mensaje);
    }
    setAltaProc(false);
  };

  // ── Cash out, ahora con credencial ────────────────────────────
  //
  // Entra por la puerta de la VENTANILLA (`/cashout/agencia`), no por la del
  // jugador: el boleto no es de la terminal, y el de mostrador no es de ningún
  // jugador. Las reglas son las mismas que para la caja —el boleto tiene que
  // ser de la rama de su agencia y esa agencia tiene que tener el cash out
  // habilitado—, así que la terminal no es una excepción a nada: es otra forma
  // de presentarse en el mismo mostrador.
  //
  // Los `cabeceraDeTerminal` no son decorativos: sin ellos es 401, y es
  // correcto que lo sea.
  const [coCode,setCoCode]=useState("");
  const [coValor,setCoValor]=useState(null);
  const [coMsg,setCoMsg]=useState("");
  const [coProc,setCoProc]=useState(false);
  const [coHecho,setCoHecho]=useState(null);
  const consultarCO=async()=>{
    if(!coCode.trim()||coProc) return;
    setCoProc(true); setCoMsg(""); setCoValor(null); setCoHecho(null);
    try{
      const r=await fetch(
        `${API}/api/betslip/${coCode.trim().toUpperCase()}/cashout/agencia`,
        {headers:cabeceraDeTerminal(agenciaCode)});
      const d=await r.json();
      if(r.status===401||r.status===403){
        // La credencial dejó de valer en el medio del turno: venció, o la
        // agencia apagó la terminal. Se borra y se vuelve a pedir el alta, en
        // vez de dejar la pantalla repitiendo un pedido condenado.
        const esDeCredencial=/credencial|apagada|ya no existe/i.test(d.detail||"");
        if(esDeCredencial){ olvidarToken(agenciaCode); setTerminal(null); }
        setCoMsg(d.detail||"Esta terminal no puede consultar el cash out");
      }
      else if(d.disponible) setCoValor(d.valor);
      else setCoMsg(d.motivo||d.detail||"Cash out no disponible");
    }catch(e){ setCoMsg("Error al consultar"); }
    setCoProc(false);
  };
  const ejecutarCO=async(destino)=>{
    if(coProc) return;
    setCoProc(true); setCoMsg("");
    try{
      const r=await fetch(
        `${API}/api/betslip/${coCode.trim().toUpperCase()}/cashout/agencia`,{
        method:"POST",
        headers:{"Content-Type":"application/json",
                 ...cabeceraDeTerminal(agenciaCode)},
        // `ejecutor` ya no se manda: el rótulo del movimiento lo pone el
        // servidor con el código de ESTA terminal. Un rótulo de auditoría que
        // elige el cliente es una firma que se puede falsificar.
        body:JSON.stringify({valor_esperado:coValor,destino}),
      });
      const d=await r.json();
      if(r.ok&&d.ok){ setCoHecho({valor:d.valor,destino:d.destino,code:d.code,
        mensaje:d.mensaje}); setCoValor(null); }
      else setCoMsg(d.detail||"No se pudo cashear");
    }catch(e){ setCoMsg("Error al cashear"); }
    setCoProc(false);
  };
  // Combos IA
  const [combos,setCombos]=useState(null);
  // Mejorar por captura
  const [imgsMejora,setImgsMejora]=useState([]);
  const [analizando,setAnalizando]=useState(false);
  const [resMejora,setResMejora]=useState(null);
  const [corrigiendo,setCorrigiendo]=useState(null);
  const aplicarCorreccion=(idx,nuevo)=>{
    setResMejora(r=>{
      const picks=r.picks.map((p,i)=>i===idx?{...p,...nuevo}:p);
      const validos=picks.filter(p=>p.odd_final);
      let t=1; validos.forEach(p=>t*=p.odd_final);
      return {...r,picks,picks_ok:validos.length,
        cuota_total:validos.length?Math.round(t*100)/100:null};
    });
    setCorrigiendo(null);
  };
  const quitarPickM=(idx)=>{
    setResMejora(r=>{
      const picks=r.picks.filter((_,i)=>i!==idx);
      const validos=picks.filter(p=>p.odd_final);
      let t=1; validos.forEach(p=>t*=p.odd_final);
      return {...r,picks,picks_total:picks.length,picks_ok:validos.length,
        cuota_total:validos.length?Math.round(t*100)/100:null};
    });
    setCorrigiendo(null);
  };
  const [errMejora,setErrMejora]=useState("");

  // Validar la agencia de la URL
  useEffect(()=>{
    if(!agenciaCode){ setErrAg("Falta el código de agencia en la dirección"); return; }
    fetch(`${API}/api/box/${agenciaCode}/valida`)
      .then(r=>r.ok?r.json():Promise.reject())
      .then(d=>setAgencia(d))
      .catch(()=>setErrAg("Agencia no encontrada"));
  },[agenciaCode]);

  // Cargar prematch
  useEffect(()=>{
    if(!agencia) return;
    fetch(`${API}/api/live/prematch`)
      .then(r=>r.ok?r.json():null)
      .then(d=>setDeportes(d?.sports||[]))
      .catch(()=>setDeportes([]));
  },[agencia]);

  // Cargar combos (IA + manuales de esta agencia) al abrir la sección
  useEffect(()=>{
    if(seccion!=="combos" || combos!==null) return;
    Promise.all([
      fetch(`${API}/api/ai/combos`).then(r=>r.ok?r.json():{combos:[]}).catch(()=>({combos:[]})),
      fetch(`${API}/api/box/${agenciaCode}/combos-manuales`)
        .then(r=>r.ok?r.json():{combos:[]}).catch(()=>({combos:[]})),
    ]).then(([ia, man])=>{
      // Los manuales primero (son los que la casa quiere destacar)
      setCombos([...(man.combos||[]), ...(ia.combos||[])]);
    });
  },[seccion, combos, agenciaCode]);

  // Cargar un combo IA al carrito
  //
  // El mercado y el nombre del emisor vienen GUARDADOS en el combo desde L4a
  // (`casino_api.py`, las tres listas blancas que escriben
  // `combos_manuales.picks`) y este mapa los tiraba, así que el combo que se
  // publicaba liquidable se vendía sin clave igual. `sel` sigue siendo la
  // etiqueta: es lo que el cajero lee y la identidad del carrito (`id`).
  //
  // Un combo publicado ANTES de L4a no los trae y queda en vacío: se vende
  // como se vendía y lo liquida una persona a mano. No se los inventa.
  const cargarCombo=(combo)=>{
    setPicks(combo.picks.map(p=>({
      id:`${p.h}-${p.a}-${p.sel}`, home:p.h, away:p.a, sel:p.sel,
      nombre:p.nombre||"", market:p.market||"",
      odd:p.odd, sport:p.sport, event_id:p.event_id, sport_key:p.sport_key,
    })));
    setSeccion("armar");
  };

  // Analizar captura de otro sitio
  const elegirImg=(e)=>{
    const files=Array.from(e.target.files||[]);
    if(!files.length) return;
    setErrMejora(""); setResMejora(null);
    files.forEach(file=>{
      if(file.size>8*1024*1024){ setErrMejora("Una imagen supera 8MB"); return; }
      const rd=new FileReader();
      rd.onload=()=>setImgsMejora(prev=>[...prev,{b64:rd.result.split(",")[1],
        tipo:file.type||"image/jpeg", preview:rd.result}]);
      rd.readAsDataURL(file);
    });
    e.target.value="";
  };
  const quitarImgMejora=(i)=>setImgsMejora(prev=>prev.filter((_,k)=>k!==i));
  const analizarImg=async()=>{
    if(!imgsMejora.length||analizando) return;
    setAnalizando(true); setErrMejora(""); setResMejora(null);
    try{
      const r=await fetch(`${API}/api/mejorar-combinada`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({imagenes:imgsMejora.map(im=>({data:im.b64,media_type:im.tipo}))}),
      });
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      const d=await r.json();
      if(!d.ok) setErrMejora(d.mensaje||"No se pudo leer la imagen");
      else setResMejora(d);
    }catch(e){ setErrMejora(e.message==="Failed to fetch"?"Sin conexión":e.message); }
    setAnalizando(false);
  };
  // Cargar al carrito los picks que sí podemos tomar de una captura
  const cargarMejora=()=>{
    const validos=(resMejora?.picks||[]).filter(p=>p.odd_final);
    // `sel` es lo que viaja a la venta, así que gana `p.nombre` —el nombre que
    // emite GR8, que lo pone el corrector— sobre `p.selection`, que es la
    // etiqueta de pantalla. `market` se agrega por lo mismo: antes este mapa no
    // lo pasaba en absoluto, así que el boleto llegaba sin mercado y el
    // reconocedor tenía que buscar el nombre en TODOS los mercados del evento
    // —el nombre pelado de un equipo lo producen el ganador a dos vías y el
    // 1X2, que son dos preguntas distintas—, y eso queda ambiguo y sin clave.
    // El `id` sigue armándose con la etiqueta: es la identidad del carrito, lo
    // que el cajero ve y toca.
    //
    // ESTE camino escribe el nombre del emisor ENCIMA de `sel`, que es lo que
    // el cajero lee en el carrito y en el boleto: para un pick corregido la
    // pantalla le muestra "Draw" y "Over 2" en vez de "Empate" y "Más de 2".
    // Queda como está a propósito: lo fija `opcionSePuedeLiquidar.test.js`, que
    // no está en las superficies de esta tanda, y moverlo cambia lo que una
    // persona lee en el mostrador. Anotado como hallazgo abierto. Los demás
    // caminos del carrito ya guardan el nombre AL LADO, en `p.nombre`, y
    // `confirmar` los acepta a los dos (`sel:p.nombre||p.sel`).
    setPicks(validos.map(p=>({
      id:`${p.home}-${p.away}-${p.selection}`, home:p.home, away:p.away,
      sel:p.nombre||p.selection, odd:p.odd_final, sport:p.market,
      market:p.market,
      event_id:p.event_id, sport_key:p.sport_key,
    })));
    setSeccion("armar"); setImgsMejora([]); setResMejora(null);
  };

  // `market` y `nombre` son el mercado y el nombre del resultado TAL COMO LOS
  // EMITE GR8, y los trae `nombresDe1x2` leyendo `ev.markets.h2h`. Este
  // `togglePick` declaraba CUATRO argumentos y no guardaba ninguno de los dos,
  // así que todo pick armado en la terminal llegaba a la venta con la etiqueta
  // de pantalla donde va la clave y sin mercado: se vendía y lo liquidaba una
  // persona a mano en el mostrador.
  //
  // Van ADEMÁS de `sel`, que sigue siendo la etiqueta —lo que el cajero lee— y
  // la identidad del carrito (`id`). Quedan vacíos cuando el diccionario del
  // emisor no tiene ese resultado; ver `nombresDe1x2`.
  const togglePick=(ev,dep,label,odd,market,nombre)=>{
    const id=`${ev.h}-${ev.a}-${label}`;
    setPicks(p=>{
      const w=p.filter(x=>x.id!==id);
      if(p.find(x=>x.id===id)) return w;
      const delMismo=p.filter(x=>x.home===ev.h&&x.away===ev.a).length;
      if(delMismo>=(bbFeature.max_picks||4)){
        alert(`Máximo ${bbFeature.max_picks||4} selecciones del mismo partido`);
        return p;
      }
      return[...w,{id,home:ev.h,away:ev.a,sel:label,odd,sport:dep,
        market:market||"",nombre:nombre||"",
        event_id:ev.id,sport_key:ev.sport_key,
        // La hora del partido viaja con el pick: la anulación la
        // necesita para saber si el evento está por empezar.
        commence_time:ev.commence_time||ev.time||""}];
    });
  };
  const tiene=(ev,label)=>picks.some(x=>x.id===`${ev.h}-${ev.a}-${label}`);
  const totBruto=picks.length?picks.reduce((a,p)=>a*p.odd,1):1;

  // Mismo partido + cotización protegida.
  //
  // El pedido lleva el partido de cada selección y la bandera sale de esa
  // misma lista. Mandar `{odd}` pelado dejaba al servidor sin con qué
  // agrupar: metía todo en un grupo `("","")` y cobraba el margen del boleto
  // entero, así que el cajero leía en voz alta una cuota MÁS BAJA que la que
  // el boleto iba a pagar.
  const picksACotizar=picks.map(p=>({odd:p.odd,home:p.h||p.home||"",away:p.a||p.away||""}));
  const mismoPartido=hayMismoPartido(picksACotizar);
  const [cuotaProt,setCuotaProt]=useState(null);
  useEffect(()=>{
    if(picks.length===0){ setCuotaProt(null); return; }
    let vivo=true;
    fetch(`${API}/api/bet-builder/cotizar`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({picks:picksACotizar,mismo_partido:mismoPartido}),
    }).then(r=>r.ok?r.json():null).then(d=>{ if(vivo&&d) setCuotaProt(d.cuota); }).catch(()=>{});
    return ()=>{vivo=false;};
    // eslint-disable-next-line
  },[JSON.stringify(picksACotizar),mismoPartido]);
  const totOdd = cuotaProt!=null ? cuotaProt : totBruto;

  const confirmar=async()=>{
    if(!picks.length||enviando) return;
    setEnviando(true); setErrEnvio("");
    try{
      const r=await fetch(`${API}/api/box/${agenciaCode}/betslip`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify(conRef("box", {picks:picks.map(p=>({
          // `sel` es lo que viaja a la VENTA, así que gana el nombre del
          // emisor sobre la etiqueta: es el único de los dos que
          // `gr8_seleccion.resolver_una` encuentra en el índice de claves de
          // GR8. Lo que el cajero LEE no cambia: el carrito y el boleto
          // dibujan `p.sel` desde el estado local, que sigue siendo la
          // etiqueta.
          home:p.home,away:p.away,sel:p.nombre||p.sel,odd:p.odd,sport:p.sport,
          // El mercado de GR8. Este cuerpo no lo mandaba nunca Y
          // `box_crear_betslip` tampoco lo leía —era el único de los tres
          // manejadores de venta que lo descartaba—, así que el arreglo son las
          // dos mitades. Sin mercado, la clave de liquidación se busca a ciegas
          // entre todos los mercados del evento y el nombre pelado de un equipo
          // sale de dos, con lo que queda ambiguo y sin clave.
          market:p.market||"",
          event_id:p.event_id,sport_key:p.sport_key,
          commence_time:p.commence_time||"",
        })), odd_total:totOdd, mismo_partido:mismoPartido})),
      });
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(mensajeDeConflicto("box", r.status, e.detail)
          ||e.detail||`Error ${r.status}`); }
      envioCumplido("box");
      const d=await r.json();
      setSlip({code:d.code, odd_total:d.odd_total,
        picks:[...picks], monto});
      setPantalla("listo");
    }catch(e){ setErrEnvio(e.message==="Failed to fetch"?"Sin conexión":e.message); }
    setEnviando(false);
  };

  const reiniciar=()=>{
    setPicks([]); setSlip(null); setPantalla("armar");
    // El monto vuelve al sugerido de la moneda de la agencia, no a
    // 1000 fijo, que en dólares no tenía sentido.
    setMonto(montoInicial(agencia?.moneda)); setMontoTocado(false);
    setBusqueda(""); setLiga(null);
  };

  // Mantener selecciones: vuelve a armar sin borrar el boleto. Sirve
  // para que dos clientes jueguen lo mismo sin rehacer la búsqueda.
  const mantenerSelecciones=()=>{
    setSlip(null); setPantalla("armar"); setBusqueda(""); setLiga(null);
  };

  // ── Estilos base full-screen ──

  const wrap={minHeight:"100dvh",background:Q.void,color:Q.text,
    fontFamily:F_BODY,
    display:"flex",flexDirection:"column"};

  // El Box se sirve en su propia ruta, asi que carga sus fuentes aparte
  const EstilosBox = () => (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap');
      *{box-sizing:border-box}
      button{font-family:inherit;-webkit-tap-highlight-color:transparent;
             touch-action:manipulation}
      button:active{opacity:.85}
      ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-thumb{background:${Q.border}}
    `}</style>
  );

  if(errAg) return(
    <div style={{...wrap,alignItems:"center",justifyContent:"center",padding:SPACING[20]}}>
      <div style={{fontSize:44,marginBottom:16}}>
        <Icon name="triangle-alert" size={44} color={Q.red}/></div>
      <div style={{fontSize:18,fontWeight:700,textAlign:"center"}}>{errAg}</div>
      <div style={{color:Q.muted,fontSize:13,marginTop:8,textAlign:"center"}}>
        Verificá la dirección de la terminal
      </div>
    </div>
  );

  if(!agencia) return(
    <div style={{...wrap,alignItems:"center",justifyContent:"center"}}>
      <div style={{color:Q.muted,fontSize:16}}>Cargando terminal...</div>
    </div>
  );

  // ── PANTALLA FINAL: código + QR ──
  if(pantalla==="listo"&&slip){
    const ret=Math.round(slip.monto*slip.odd_total);
    const qrData=encodeURIComponent(slip.code);
    return(
      <div style={{...wrap,alignItems:"center",justifyContent:"center",padding:SPACING[24]}}>
        <div style={{maxWidth:460,width:"100%",textAlign:"center"}}>
          <div style={{fontSize:40,marginBottom:10}}>
            <Icon name="ticket" size={40} color={Q.gold}/></div>
          <div style={{fontSize:20,fontWeight:700,marginBottom:4}}>
            ¡Tu apuesta está lista!
          </div>
          <div style={{color:Q.muted,fontSize:14,marginBottom:20}}>
            Mostrá este código en la caja para pagar
          </div>

          <div style={{background:"#fff",borderRadius:RADII.xl,padding:SPACING[24],
            marginBottom:20}}>
            <img alt="QR"
              src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${qrData}`}
              style={{width:220,height:220,display:"block",margin:"0 auto"}}/>
            <div style={{color:"#000",fontWeight:900,fontSize:32,marginTop:14,
              letterSpacing:2,fontFamily:"'Courier New',monospace"}}>{slip.code}</div>
          </div>

          <div style={{background:Q.dark,borderRadius:RADII.lg,padding:SPACING[20],
            border:`1px solid ${Q.border}`,marginBottom:20,textAlign:"left"}}>
            {slip.picks.map((p,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",
                padding:"8px 0",gap:SPACING[12],
                borderBottom:i<slip.picks.length-1?`1px solid ${Q.dim}`:"none"}}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:600,overflow:"hidden",
                    textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.home} vs {p.away}</div>
                  <div style={{color:Q.muted,fontSize:12}}>{p.sel}</div>
                </div>
                <span style={{color:Q.cyan,fontWeight:700,fontSize:15,flexShrink:0}}>
                  {fmt(p.odd)}</span>
              </div>
            ))}
            <div style={{display:"flex",justifyContent:"space-between",
              marginTop:12,paddingTop:SPACING[12],borderTop:`2px solid ${Q.violet}44`}}>
              <span style={{color:Q.muted,fontSize:14}}>Cuota total</span>
              <span style={{color:Q.gold,fontWeight:900,fontSize:20}}>{fmt(slip.odd_total)}x</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
              <span style={{color:Q.muted,fontSize:14}}>Monto sugerido</span>
              <span style={{fontWeight:700,fontSize:16}}>{money(slip.monto, mon)}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
              <span style={{color:Q.muted,fontSize:14}}>Retorno potencial</span>
              <span style={{color:Q.green,fontWeight:900,fontSize:18}}>{money(ret, mon)}</span>
            </div>
          </div>

          <div style={{color:Q.amber,fontSize:12,marginBottom:20,lineHeight:1.4}}>
            <Icon name="clock-3" size={13}/> El código vale por 24 horas. El monto final lo confirmás en la caja.
          </div>

          {picks.length>0&&(
            <button onClick={mantenerSelecciones} style={{width:"100%",
              background:`${Q.violet}22`,border:`1px solid ${Q.violet}`,
              borderRadius:RADII.lg,padding:"16px",cursor:"pointer",
              color:Q.cyan,fontWeight:700,fontSize:15,marginBottom:10}}>
              <Repeat size={15}/> Mantener selecciones ({picks.length})
            </button>
          )}

          <button onClick={reiniciar} style={{width:"100%",
            background:`linear-gradient(135deg,${Q.violet},${Q.cyan})`,
            border:"none",borderRadius:RADII.lg,padding:"16px",cursor:"pointer",
            color:"#fff",fontWeight:700,fontSize:16}}>
            Hacer otra apuesta
          </button>
        </div>
      </div>
    );
  }

  // ── PANTALLA ARMAR ──
  const filtrados=(deportes||[])
    .filter(d=>!liga||d.name===liga)
    .map(d=>({...d,events:(d.events||[]).filter(ev=>{
      const q=busqueda.trim().toLowerCase();
      return !q||`${ev.h} ${ev.a}`.toLowerCase().includes(q);
    })}))
    .filter(d=>d.events.length>0);

  return(
    <div style={wrap}>
      <EstilosBox/>
      {/* Encabezado */}
      <div style={{background:Q.deep,borderBottom:`1px solid ${Q.border}`,
        padding:"16px 20px",display:"flex",alignItems:"center",
        justifyContent:"space-between",position:"sticky",top:0,zIndex:50}}>
        <div>
          <BrandMark size={26}/>
          <div style={{color:Q.muted,fontSize:12,marginTop:3}}>
            {agencia.name} · Terminal</div>
        </div>
        {picks.length>0&&(
          <div style={{textAlign:"right"}}>
            <div style={{color:Q.muted,fontWeight:600,fontSize:12,fontFamily:F_BODY}}>
              {picks.length} {picks.length===1?"pick":"picks"}</div>
            <div style={{color:Q.gold,fontSize:24,fontWeight:700,fontFamily:F_NUM,
              lineHeight:1.05}}>{fmt(totOdd)}x</div>
          </div>
        )}
      </div>

      {/* Pestañas */}
      <div style={{display:"flex",gap:SPACING[8],padding:"12px 20px 0",
        maxWidth:720,margin:"0 auto",width:"100%"}}>
        {[["armar","Armar"],["combos","Combos"],["mejorar","Bet Best"],["cashout","Cash out"]].map(([k,l])=>{
          const on = seccion===k;
          const bb = k==="mejorar";   // la funcion insignia se lleva el dorado
          return(
          <button key={k} onClick={()=>setSeccion(k)} style={{
            flex:bb?1.25:1,
            background: bb ? (on?`linear-gradient(145deg,#FFE07A,${Q.goldBg})`:`${Q.gold}1A`)
                           : (on?Q.violet:Q.inset),
            border:`1px solid ${bb?Q.gold:(on?Q.violet:Q.border)}`,borderRadius:RADII.md,
            padding:"12px 8px",cursor:"pointer",
            color: bb ? (on?"#1A1200":Q.gold) : (on?"#fff":Q.muted),
            fontSize:14,fontWeight:on||bb?700:500,fontFamily:F_BODY,
            display:"flex",alignItems:"center",justifyContent:"center",gap:SPACING[8]}}>
            {bb&&(
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                stroke={on?"#1A1200":Q.gold} strokeWidth="1.9"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8.5A1.5 1.5 0 014.5 7h2L8 5h8l1.5 2h2A1.5 1.5 0 0121 8.5v9a1.5 1.5 0 01-1.5 1.5h-15A1.5 1.5 0 013 17.5z"/>
                <circle cx="12" cy="13" r="3.5"/>
              </svg>
            )}
            {l}</button>
          );
        })}
      </div>

      {/* Cuerpo scroll */}
      <div style={{flex:1,overflowY:"auto",padding:"16px 20px 40px",
        maxWidth:720,margin:"0 auto",width:"100%"}}>

        {/* ── SECCIÓN COMBOS ── */}
        {seccion==="combos"&&(
          <div>
            <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>Combos sugeridos</div>
            <div style={{color:Q.muted,fontSize:12,marginBottom:14}}>
              Tocá uno para cargarlo y sacar tu código
            </div>
            {combos===null&&<div style={{color:Q.muted,textAlign:"center",
              padding:SPACING[32],fontSize:15}}>Cargando combos...</div>}
            {combos&&combos.length===0&&(
              <div style={{textAlign:"center",padding:SPACING[32]}}>
                <div style={{marginBottom:8}}><Moon size={30} color={Q.muted}/></div>
                <div style={{color:Q.muted,fontSize:14}}>No hay combos ahora</div>
              </div>
            )}
            {(combos||[]).map((combo,ci)=>(
              <div key={combo.id||ci} style={{background:Q.dark,borderRadius:RADII.lg,
                padding:SPACING[16],marginBottom:12,border:`1px solid ${combo.tagColor||Q.border}44`}}>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"flex-start",marginBottom:10,gap:SPACING[8]}}>
                  <div style={{minWidth:0,flex:1}}>
                    <div style={{fontWeight:700,fontSize:15}}>{combo.name}</div>
                    <div style={{color:combo.tagColor||Q.muted,fontSize:12,
                      marginTop:2}}>{combo.tag}</div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{color:Q.gold,fontWeight:900,fontSize:19}}>{fmt(combo.odd_total)}x</div>
                    <div style={{color:Q.muted,fontSize:12}}>{combo.picks.length} picks</div>
                  </div>
                </div>
                {combo.picks.slice(0,4).map((p,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",
                    padding:"4px 0",gap:SPACING[8],fontSize:12}}>
                    <span style={{color:Q.muted,overflow:"hidden",
                      textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {p.h} vs {p.a} · {p.sel}</span>
                    <span style={{color:Q.cyan,fontWeight:700,flexShrink:0}}>{fmt(p.odd)}</span>
                  </div>
                ))}
                <button onClick={()=>cargarCombo(combo)} style={{width:"100%",
                  marginTop:10,background:`linear-gradient(135deg,${combo.tagColor||Q.violet},${Q.violet})`,
                  border:"none",borderRadius:RADII.md,padding:"12px",cursor:"pointer",
                  color:"#fff",fontWeight:700,fontSize:14}}>
                  Usar este combo</button>
              </div>
            ))}
          </div>
        )}

        {/* ── SECCIÓN CASH OUT ──
            La terminal lo cierra con SU credencial, por la puerta de la
            ventanilla. Tres estados, y cada uno dice qué falta:
              · sin credencial  → el campo para darla de alta
              · con credencial y sin permiso → dónde pedirlo
              · con credencial y con permiso → el botón */}
        {seccion==="cashout"&&(
          <div>
            <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>
              <Icon name="wallet-cards" size={16}/> Cash out</div>
            <div style={{color:Q.muted,fontSize:12,marginBottom:14,lineHeight:1.4}}>
              Podés retirar antes de que termine el partido, al valor actual
              en vivo.
            </div>

            {credProbando&&(
              <div style={{color:Q.muted,fontSize:14,textAlign:"center",
                padding:SPACING[24]}}>Verificando la terminal...</div>
            )}

            {/* ── SIN CREDENCIAL: el alta ──
                No se muestra un cartel rojo ni un botón que va a dar 401: se
                muestra exactamente lo que falta y quién lo da. El que está en
                el mostrador tiene que poder resolverlo sin llamar a nadie más
                que a su agencia. */}
            {!credProbando&&!terminal&&(
              <div style={{background:`${Q.violet}14`,
                border:`1px solid ${Q.violet}`,borderRadius:RADII.lg,
                padding:SPACING[20],lineHeight:1.5}}>
                <div style={{color:Q.text,fontWeight:800,fontSize:14,
                  marginBottom:8}}>Falta dar de alta esta terminal</div>
                <div style={{color:Q.muted,fontSize:13,marginBottom:14}}>
                  Para cerrar un cash out, la pantalla necesita su propia
                  credencial. Pedile a la agencia que genere un código de alta
                  desde su panel, en Terminales, y escribilo acá. Se hace una
                  sola vez.
                </div>
                <input value={codigoAlta}
                  onChange={e=>setCodigoAlta(e.target.value.trim())}
                  onKeyDown={e=>e.key==="Enter"&&darDeAlta()}
                  placeholder="TA-…" autoComplete="off" spellCheck={false}
                  style={{width:"100%",background:ov(0.06),
                    border:`1.5px solid ${Q.border}`,borderRadius:RADII.md,
                    padding:"12px 16px",color:Q.text,fontSize:16,
                    fontWeight:600,marginBottom:10}}/>
                <button onClick={darDeAlta} disabled={!codigoAlta||altaProc}
                  style={{width:"100%",background:`linear-gradient(135deg,${Q.violet},${Q.cyan})`,
                    border:"none",borderRadius:RADII.md,padding:SPACING[16],
                    color:"#fff",fontWeight:700,fontSize:14,
                    opacity:(!codigoAlta||altaProc)?0.55:1,
                    cursor:(!codigoAlta||altaProc)?"default":"pointer"}}>
                  {altaProc?"Dando de alta…":"Dar de alta la terminal"}</button>
                {altaMsg&&<div style={{color:Q.red,fontSize:13,marginTop:10,
                  textAlign:"center",lineHeight:1.45}}>{altaMsg}</div>}
                <div style={{color:Q.dim,fontSize:12,marginTop:14,
                  paddingTop:SPACING[12],
                  borderTop:`1px solid ${Q.border}`,lineHeight:1.5}}>
                  Mientras tanto, el cash out se pide desde la cuenta del
                  jugador —en la app o en el sitio— o en la caja.
                </div>
              </div>
            )}

            {/* ── CON CREDENCIAL Y SIN PERMISO ──
                La terminal es alguien, pero su agencia no tiene el cash out
                habilitado. No es un problema de la pantalla y el texto lo dice:
                se arregla con el administrador, no tipeando otro código. */}
            {!credProbando&&terminal&&!terminal.puede_cashout&&(
              <div style={{background:`${Q.gold}12`,
                border:`1px solid ${Q.gold}`,borderRadius:RADII.lg,
                padding:SPACING[20],lineHeight:1.5}}>
                <div style={{color:Q.gold,fontWeight:800,fontSize:14,
                  marginBottom:10}}>El cash out no está habilitado</div>
                <div style={{color:Q.text,fontSize:13}}>
                  {terminal.agencia} todavía no tiene el cash out habilitado.
                  Lo habilita el administrador.
                </div>
                <div style={{color:Q.muted,fontSize:12,marginTop:12}}>
                  Terminal {terminal.nombre} · {terminal.codigo}
                </div>
              </div>
            )}

            {/* ── CON CREDENCIAL Y CON PERMISO: el botón de vuelta ── */}
            {!credProbando&&terminal&&terminal.puede_cashout&&(<>
              <div style={{color:Q.dim,fontSize:12,marginBottom:12}}>
                Terminal {terminal.nombre} · {terminal.codigo}
              </div>

              {!coHecho&&(
                <div style={{display:"flex",gap:SPACING[8],marginBottom:14}}>
                  <input value={coCode}
                    onChange={e=>setCoCode(e.target.value.toUpperCase())}
                    onKeyDown={e=>e.key==="Enter"&&consultarCO()}
                    placeholder="QP-47829"
                    style={{flex:1,background:ov(0.06),
                      border:`1.5px solid ${Q.border}`,borderRadius:RADII.md,
                      padding:"12px 16px",color:Q.text,fontSize:20,
                      fontWeight:700,letterSpacing:2}}/>
                  <button onClick={consultarCO} disabled={!coCode||coProc}
                    style={{background:`${Q.gold}33`,
                      border:`1.5px solid ${Q.gold}`,borderRadius:RADII.md,
                      padding:"0 20px",color:Q.gold,fontWeight:700,
                      fontSize:14,cursor:"pointer"}}>
                    {coProc?"...":"VER"}</button>
                </div>
              )}

              {coMsg&&<div style={{color:Q.red,fontSize:13,textAlign:"center",
                marginBottom:12,lineHeight:1.45}}>{coMsg}</div>}

              {coValor!=null&&!coHecho&&(
                <div style={{background:`${Q.gold}12`,
                  border:`1px solid ${Q.gold}`,borderRadius:RADII.lg,
                  padding:SPACING[20],marginBottom:12,textAlign:"center"}}>
                  <div style={{color:Q.muted,fontSize:12,
                    textTransform:"uppercase",letterSpacing:1}}>
                    Retirás ahora</div>
                  <div style={{color:Q.gold,fontWeight:900,fontSize:36,
                    margin:"6px 0"}}>
                    {money(coValor, mon)}</div>
                  <div style={{color:Q.muted,fontSize:12,marginBottom:16}}>
                    ¿Cómo querés cobrarlo?</div>
                  <button onClick={()=>ejecutarCO("cuenta")} disabled={coProc}
                    style={{width:"100%",
                      background:`linear-gradient(135deg,${Q.violet},${Q.cyan})`,
                      border:"none",borderRadius:RADII.md,padding:SPACING[16],
                      color:"#fff",fontWeight:700,fontSize:14,
                      cursor:"pointer",marginBottom:8}}>
                    A mi cuenta (si tengo)</button>
                  <button onClick={()=>ejecutarCO("mostrador")} disabled={coProc}
                    style={{width:"100%",background:`${Q.gold}22`,
                      border:`1px solid ${Q.gold}`,borderRadius:RADII.md,
                      padding:SPACING[16],color:Q.gold,fontWeight:700,
                      fontSize:14,cursor:"pointer"}}>
                    Código para cobrar en mostrador</button>
                  <button onClick={()=>{setCoValor(null);setCoCode("");}}
                    style={{marginTop:10,background:"transparent",
                      border:"none",color:Q.muted,fontSize:12,
                      cursor:"pointer"}}>Cancelar</button>
                </div>
              )}

              {coHecho&&(
                <div style={{background:`${Q.green}12`,
                  border:`1px solid ${Q.green}`,borderRadius:RADII.lg,
                  padding:SPACING[24],textAlign:"center"}}>
                  <div style={{fontSize:48,marginBottom:8}}>
                    <Icon name={coHecho.destino==="cuenta"?"circle-check":"receipt-text"}
                      size={48} color={Q.green}/></div>
                  <div style={{color:Q.green,fontWeight:800,fontSize:18,
                    marginBottom:6}}>Cash out confirmado</div>
                  <div style={{color:Q.text,fontWeight:900,fontSize:30,
                    marginBottom:8}}>
                    {money(coHecho.valor, mon)}</div>
                  {coHecho.destino==="cuenta"?(
                    <div style={{color:Q.muted,fontSize:13}}>
                      Se acreditó a tu cuenta.</div>
                  ):(
                    <div>
                      <div style={{color:Q.muted,fontSize:13,marginBottom:10}}>
                        Mostrá este código en la caja para cobrar en efectivo:</div>
                      <div style={{background:ov(0.06),
                        border:`1px solid ${Q.gold}`,borderRadius:RADII.lg,
                        padding:"16px",color:Q.gold,fontWeight:900,fontSize:26,
                        letterSpacing:3}}>{coHecho.code}</div>
                    </div>
                  )}
                  <button onClick={()=>{setCoHecho(null);setCoCode("");
                    setCoValor(null);}}
                    style={{marginTop:16,background:`${Q.violet}22`,
                      border:`1px solid ${Q.violet}`,borderRadius:RADII.md,
                      padding:"12px 24px",color:Q.violet2||Q.violet,
                      fontWeight:700,fontSize:13,cursor:"pointer"}}>
                    Listo</button>
                </div>
              )}
            </>)}
          </div>
        )}

        {/* ── SECCIÓN MEJORAR ── */}
        {seccion==="mejorar"&&(
          <div>
            <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>Mejorá tu apuesta</div>
            <div style={{color:Q.muted,fontSize:12,marginBottom:14,lineHeight:1.4}}>
              Subí la captura de una apuesta de otro sitio. La leemos y la
              replicamos con nuestras cuotas.
            </div>
            {imgsMejora.length>0&&(
              <div style={{display:"flex",gap:SPACING[8],flexWrap:"wrap",marginBottom:10}}>
                {imgsMejora.map((im,i)=>(
                  <div key={i} style={{position:"relative"}}>
                    <img src={im.preview} alt={"f"+i} style={{width:64,height:64,
                      objectFit:"cover",borderRadius:RADII.md,border:`1px solid ${Q.border}`}}/>
                    <button onClick={()=>quitarImgMejora(i)} style={{position:"absolute",
                      top:-6,right:-6,width:20,height:20,borderRadius:"50%",
                      background:Q.pink,border:"none",color:"#fff",fontSize:12,
                      cursor:"pointer",lineHeight:1}}>✕</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{display:"flex",gap:SPACING[12],marginBottom:10}}>
              <label style={{flex:1,border:`2px dashed ${Q.border}`,borderRadius:RADII.lg,
                padding:"24px 12px",textAlign:"center",cursor:"pointer"}}>
                <input type="file" accept="image/*" capture="environment"
                  onChange={elegirImg} style={{display:"none"}}/>
                <div style={{fontSize:26,marginBottom:5}}>
                  <Icon name="camera" size={26}/></div>
                <div style={{fontWeight:700,fontSize:12}}>Sacar foto</div>
              </label>
              <label style={{flex:1,border:`2px dashed ${Q.border}`,borderRadius:RADII.lg,
                padding:"24px 12px",textAlign:"center",cursor:"pointer"}}>
                <input type="file" accept="image/*" multiple onChange={elegirImg}
                  style={{display:"none"}}/>
                <div style={{marginBottom:5}}><ImageIcon size={26}/></div>
                <div style={{fontWeight:700,fontSize:12}}>
                  {imgsMejora.length>0?"Agregar más":"Galería"}</div>
              </label>
            </div>
            {imgsMejora.length>0&&(
              <button onClick={analizarImg} disabled={analizando} style={{width:"100%",
                background:analizando?ov(0.06):`linear-gradient(135deg,${Q.cyan},${Q.violet})`,
                border:"none",borderRadius:RADII.lg,padding:"16px",marginBottom:4,
                cursor:analizando?"wait":"pointer",color:analizando?Q.muted:"#fff",
                fontWeight:700,fontSize:14}}>
                {analizando?"Leyendo...":<><Icon name="search" size={15}/>{` Analizar ${imgsMejora.length} foto${imgsMejora.length>1?"s":""}`}</>}</button>
            )}
            {errMejora&&<div style={{color:Q.pink,fontSize:13,marginTop:10}}>{errMejora}</div>}
            {analizando&&<div style={{color:Q.violet2,fontSize:13,textAlign:"center",
              padding:SPACING[16]}}>La IA está leyendo tu apuesta...</div>}
            {resMejora&&(
              <div style={{marginTop:14}}>
                <div style={{color:Q.muted,fontSize:12,marginBottom:10}}>
                  Leímos {resMejora.picks_total} · podemos tomar {resMejora.picks_ok}
                </div>
                {resMejora.faltan_picks&&(
                  <div style={{background:`${Q.pink}12`,border:`1px solid ${Q.pink}66`,
                    borderRadius:RADII.md,padding:"8px 12px",marginBottom:10,color:Q.pink,
                    fontSize:12,lineHeight:1.4}}>
                    <Icon name="triangle-alert" size={13}/> El cupón marca cuota {fmt(resMejora.total_odd_cupon)} pero con lo
                    leído no llegamos. Falta algún partido — agregá otra foto.
                  </div>
                )}
                {resMejora.picks.map((p,i)=>{
                  const c=p.odd_final?(p.ajustada?Q.amber:Q.green):Q.red;
                  return(
                  <div key={i} style={{background:Q.dark,borderRadius:RADII.lg,padding:SPACING[12],
                    marginBottom:8,border:`1px solid ${c}44`}}>
                    <div style={{display:"flex",justifyContent:"space-between",gap:SPACING[8]}}>
                      <div style={{minWidth:0,flex:1}}>
                        <div style={{fontSize:13,fontWeight:600,overflow:"hidden",
                          textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.home} vs {p.away}</div>
                        <div style={{color:Q.muted,fontSize:12}}>{p.selection}</div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0,fontSize:12}}>
                        {p.odd_original&&<div style={{color:Q.dim}}>Origen {fmt(p.odd_original)}</div>}
                        {p.odd_final?<div style={{color:c,fontWeight:700}}>
                          Nuestra {fmt(p.odd_final)}{p.ajustada?" ↑":""}</div>
                          :<div style={{color:Q.red,fontSize:12}}>No disponible</div>}
                      </div>
                    </div>
                    <button onClick={()=>setCorrigiendo(corrigiendo===i?null:i)} style={{
                      marginTop:8,background:corrigiendo===i?`${Q.cyan}22`:"transparent",
                      border:`1px solid ${corrigiendo===i?Q.cyan:Q.dim}`,borderRadius:RADII.md,
                      padding:"8px 12px",cursor:"pointer",color:corrigiendo===i?Q.cyan:Q.muted,
                      fontSize:12,fontWeight:700}}>
                      {corrigiendo===i?"✕ Cerrar":<><Pencil size={12}/> Está mal / Corregir</>}</button>
                    {corrigiendo===i&&(
                      <CorregirPickBox pick={p}
                        onAplicar={(nuevo)=>aplicarCorreccion(i,nuevo)}
                        onQuitar={()=>quitarPickM(i)}/>
                    )}
                  </div>
                  );
                })}
                {resMejora.picks_ok>0&&(
                  <button onClick={cargarMejora} style={{width:"100%",marginTop:6,
                    background:`linear-gradient(135deg,${Q.green},#00a854)`,
                    border:"none",borderRadius:RADII.lg,padding:"16px",cursor:"pointer",
                    color:"#04120a",fontWeight:900,fontSize:14}}>
                    Cargar {resMejora.picks_ok} selecciones · {fmt(resMejora.cuota_total)}x</button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── SECCIÓN ARMAR ── */}
        {seccion==="armar"&&<>
        <div style={{fontSize:15,fontWeight:700,marginBottom:12}}>
          Elegí tus partidos
        </div>

        {bbFeature.activo&&(
          <div style={{background:`${Q.violet}12`,border:`1px solid ${Q.violet}55`,borderRadius:RADII.md,
            padding:"8px 12px",marginBottom:12,display:"flex",alignItems:"center",gap:SPACING[8]}}>
            <Wrench size={16}/>
            <div style={{color:Q.muted,fontSize:12,fontFamily:F_BODY}}>
              <b style={{color:Q.violet}}>Bet Builder activo</b> · combiná hasta {bbFeature.max_picks||4} mercados del mismo partido</div>
          </div>
        )}

        {/* Buscador */}
        <AvisosBanner destino="box" agenciaCode={agenciaCode}/>
        <DesafiosBox agenciaCode={agenciaCode}/>

        {deportes&&deportes.length>0&&(
          <div style={{display:"flex",alignItems:"center",gap:SPACING[12],
            background:ov(0.05),border:`1px solid ${Q.border}`,
            borderRadius:RADII.lg,padding:"12px 16px",marginBottom:10}}>
            <Icon name="search" size={16} color={Q.muted}/>
            <input value={busqueda} onChange={e=>setBusqueda(e.target.value)}
              placeholder="Buscar equipo..."
              style={{background:"transparent",border:"none",color:Q.text,
                fontSize:16,flex:1,minWidth:0,outline:"none"}}/>
          </div>
        )}

        {/* Armar una combinada al momento, para el cliente que no sabe
            qué jugar. El cupo es de la agencia porque acá no hay
            cliente identificado. */}
        {picks.length===0&&deportes&&deportes.length>0&&(
          <div style={{background:ov(0.04),border:`1px solid ${Q.border}`,
            borderRadius:RADII.lg,padding:"12px 16px",marginBottom:10}}>
            <div style={{color:Q.text,fontSize:13.5,fontWeight:700,
              marginBottom:3}}><Dices size={13}/> Armar combinada</div>
            <div style={{color:Q.muted,fontSize:12,marginBottom:9,
              lineHeight:1.4}}>
              El sistema la arma con partidos de hoy.
              {genCupo&&` Quedan ${genCupo.quedan} de ${genCupo.tope} hoy.`}</div>
            <div style={{display:"flex",gap:SPACING[8]}}>
              {[["seguro",<><Icon name="shield-check" size={13}/> Seguro</>],["equilibrado",<><Scale size={13}/> Medio</>],
                ["arriesgado",<><Rocket size={13}/> Fuerte</>]].map(([k,l])=>(
                <button key={k} onClick={()=>generarCombo(k)}
                  disabled={genProc||(genCupo&&genCupo.quedan<=0)}
                  style={{flex:1,background:ov(0.05),
                    border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                    padding:"12px 4px",cursor:"pointer",
                    color:(genCupo&&genCupo.quedan<=0)?Q.dim:Q.cyan,
                    fontSize:12,fontWeight:600}}>
                  {genProc?"…":l}</button>
              ))}
            </div>
          </div>
        )}

        {/* Chips de liga */}
        {deportes&&deportes.length>0&&(
          <div style={{display:"flex",gap:SPACING[8],overflowX:"auto",paddingBottom:SPACING[4],
            marginBottom:12}}>
            <button onClick={()=>setLiga(null)} style={{
              background:!liga?`linear-gradient(135deg,${Q.violet}44,${Q.cyan}22)`:ov(0.04),
              border:`1px solid ${!liga?Q.cyan:Q.border}`,borderRadius:RADII.xl,
              padding:"8px 16px",cursor:"pointer",color:!liga?Q.cyan:Q.muted,
              fontSize:13,fontWeight:!liga?700:400,whiteSpace:"nowrap",flexShrink:0}}>
              Todas</button>
            {deportes.map(d=>(
              <button key={d.name} onClick={()=>setLiga(d.name)} style={{
                background:liga===d.name?`linear-gradient(135deg,${Q.violet}44,${Q.cyan}22)`:ov(0.04),
                border:`1px solid ${liga===d.name?Q.cyan:Q.border}`,borderRadius:RADII.xl,
                padding:"8px 16px",cursor:"pointer",color:liga===d.name?Q.cyan:Q.muted,
                fontSize:13,fontWeight:liga===d.name?700:400,whiteSpace:"nowrap",
                flexShrink:0}}>{d.icon} {d.name}</button>
            ))}
          </div>
        )}

        {!deportes&&(
          <div style={{color:Q.muted,textAlign:"center",padding:SPACING[40],fontSize:15}}>
            Cargando partidos...
          </div>
        )}
        {deportes&&deportes.length===0&&(
          <div style={{textAlign:"center",padding:SPACING[40]}}>
            <div style={{marginBottom:10}}><Inbox size={34} color={Q.muted}/></div>
            <div style={{color:Q.muted,fontSize:15}}>No hay partidos disponibles ahora</div>
          </div>
        )}

        {/* Eventos */}
        {filtrados.map(d=>(
          <div key={d.name} style={{marginBottom:20}}>
            <div style={{color:Q.violet2,fontWeight:700,fontSize:14,marginBottom:10}}>
              {d.icon} {d.name}</div>
            {d.events.map(ev=>(
              <div key={ev.id||(ev.h+ev.a)} style={{background:Q.dark,
                borderRadius:RADII.lg,padding:SPACING[16],marginBottom:10,
                border:`1px solid ${Q.border}`}}>
                <div style={{display:"flex",alignItems:"center",gap:SPACING[12],
                  marginBottom:12}}>
                  <TeamLogo name={ev.h} size={28}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:15,overflow:"hidden",
                      textOverflow:"ellipsis"}}>{ev.h} vs {ev.a}</div>
                    <div style={{color:Q.muted,fontSize:12}}>{horaLocal(ev)}</div>
                  </div>
                  <TeamLogo name={ev.a} size={28}/>
                </div>
                <div style={{display:"flex",gap:SPACING[8]}}>
                  {/* La etiqueta NO se toca: el cajero sigue leyendo
                      "Boca gana" y "Empate". Lo que se agrega es el nombre
                      del emisor y su mercado, que vienen de `nombresDe1x2`
                      leyendo el diccionario del catálogo y van vacíos cuando
                      ese diccionario no tiene el resultado. */}
                  {[{label:ev.h+" gana",odd:ev.odds?.L,...nombresDe1x2(ev).L},
                    ev.odds?.E?{label:"Empate",odd:ev.odds.E,
                                ...nombresDe1x2(ev).E}:null,
                    {label:ev.a+" gana",odd:ev.odds?.V,...nombresDe1x2(ev).V}]
                    .filter(Boolean).filter(o=>o.odd).map(opt=>{
                    const sel=tiene(ev,opt.label);
                    return(
                    <button key={opt.label}
                      onClick={()=>togglePick(ev,d.name,opt.label,opt.odd,
                        opt.market,opt.nombre)}
                      style={{flex:1,minWidth:0,
                        background:sel?`linear-gradient(135deg,${Q.violet}44,${Q.cyan}22)`:ov(0.04),
                        border:`2px solid ${sel?Q.cyan:Q.border}`,borderRadius:RADII.lg,
                        padding:"12px 8px",cursor:"pointer",textAlign:"center"}}>
                      <div style={{color:Q.muted,fontSize:12,overflow:"hidden",
                        textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{opt.label}</div>
                      <div style={{color:sel?Q.cyan:Q.text,fontWeight:700,
                        fontSize:18}}>{opt.odd}</div>
                    </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}
        </>}
      </div>

      {/* Barra inferior fija con el resumen */}
      {/* QR para que el cliente arme su boleto en el teléfono */}
      <QRTerminal agenciaCode={agenciaCode}/>

      {picks.length>0&&(
        <div style={{position:"fixed",bottom:0,left:0,right:0,
          background:`linear-gradient(0deg,${Q.void} 85%,transparent)`,
          padding:"16px 20px calc(16px + env(safe-area-inset-bottom))",zIndex:60}}>
          <div style={{maxWidth:720,margin:"0 auto"}}>
            {errEnvio&&<div style={{color:Q.pink,fontSize:13,marginBottom:8,
              textAlign:"center"}}>{errEnvio}</div>}
            <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
              {montosDe(agencia?.moneda).map(v=>(
                <button key={v} onClick={()=>setMonto(v)} style={{flex:1,
                  background:monto===v?`${Q.violet}33`:ov(0.04),
                  border:`1px solid ${monto===v?Q.violet:Q.border}`,borderRadius:RADII.md,
                  padding:"12px 4px",cursor:"pointer",color:monto===v?Q.cyan:Q.muted,
                  fontSize:13,fontWeight:monto===v?700:400}}>
                  {v>=1000?`$${v/1000}K`:`$${v}`}</button>
              ))}
            </div>
            {/* Campo libre: los botones son atajos, no un límite */}
            <div style={{display:"flex",alignItems:"center",gap:SPACING[8],marginBottom:10}}>
              <span style={{color:Q.muted,fontSize:12,flexShrink:0}}>Otro monto:</span>
              <input type="number" inputMode="numeric" min="0" value={monto}
                onChange={e=>{setMonto(Math.max(0,Number(e.target.value)||0));
                              setMontoTocado(true);}}
                aria-label="Monto a apostar"
                style={{flex:1,minWidth:0,background:Q.inset,
                  border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
                  color:Q.text,fontSize:15,fontWeight:700,fontFamily:F_NUM}}/>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",marginBottom:10}}>
              <div>
                <span style={{color:Q.muted,fontSize:13}}>{picks.length} picks · </span>
                <span style={{color:Q.gold,fontWeight:700,fontSize:15}}>{fmt(totOdd)}x</span>
              </div>
              <div style={{color:Q.green,fontSize:14}}>
                Retorno: <span style={{fontWeight:700}}>{money(Math.round(monto*totOdd), mon)}</span>
              </div>
            </div>
            <div style={{display:"flex",gap:SPACING[12]}}>
              <button onClick={()=>setPicks([])} style={{
                background:"transparent",border:`1px solid ${Q.border}`,
                borderRadius:RADII.lg,padding:"0 20px",cursor:"pointer",color:Q.muted,
                fontSize:14}}>Limpiar</button>
              <button onClick={confirmar} disabled={enviando} style={{flex:1,
                background:enviando?ov(0.06):`linear-gradient(135deg,${Q.violet},${Q.cyan})`,
                border:"none",borderRadius:RADII.lg,padding:"16px",
                cursor:enviando?"wait":"pointer",color:enviando?Q.muted:"#fff",
                fontWeight:700,fontSize:16}}>
                {enviando?"Generando...":"Generar código de apuesta"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
